import type { AppPopupConfig } from '@/features/appStatus/types/appStatus';
import { StatusVisual } from '@/features/appStatus/components/StatusVisual';
import {
  normalizeStatusType,
  statusEyebrow,
} from '@/features/appStatus/utils/normalizeStatusType';

type AppStatusPageProps = {
  config: AppPopupConfig;
  onClose?: () => void;
};

export function AppStatusPage({ config, onClose }: AppStatusPageProps) {
  const type = normalizeStatusType(config.type);
  const eyebrow = statusEyebrow(type);

  return (
    <div className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#FBFCFC] text-[#0F1817]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-[#119C84]/10 blur-3xl" />
        <div className="absolute right-0 bottom-10 h-80 w-80 rounded-full bg-[#1B7FA8]/8 blur-3xl" />
      </div>

      {config.isCloseable && onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close notice"
          className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-[#E2E9E7] bg-white text-[#7C8B88] transition-colors hover:text-[#0F1817]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}

      <div className="relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        {/* Use <a> — this page can render outside RouterProvider via AppStatusGate */}
        <a href="/" className="mb-10 inline-flex items-center">
          <img
            src="/screenista-logo.svg"
            alt="Screenista"
            className="h-9 w-auto max-w-[180px] object-contain"
          />
        </a>

        <StatusVisual type={type} />

        <p
          className="mb-3 text-[11px] font-medium tracking-[0.12em] text-[#7C8B88] uppercase"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {eyebrow}
        </p>

        <h1
          className="m-0 max-w-lg text-[28px] font-semibold leading-tight tracking-[-0.02em] text-[#0F1817] sm:text-[34px]"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {config.title || eyebrow}
        </h1>

        <p className="mt-4 mb-0 max-w-md text-[15px] leading-relaxed text-[#3E524F] sm:text-base">
          {config.message || 'We will be back shortly. Thanks for your patience.'}
        </p>

        <div className="mt-10 flex items-center justify-center gap-1.5">
          <span className="h-2 w-2 animate-bounce rounded-full bg-[#119C84] [animation-delay:-0.3s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-[#119C84] [animation-delay:-0.15s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-[#119C84]" />
        </div>

        {config.isCloseable && onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="signup-btn mt-10 inline-flex h-[46px] items-center justify-center rounded-[9px] border-none px-6 text-[14.5px] font-semibold text-white"
          >
            Continue to Screenista
          </button>
        ) : (
          <p className="mt-10 text-[12px] text-[#7C8B88]">
            This notice will clear automatically when the service is ready.
          </p>
        )}
      </div>
    </div>
  );
}
